import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { toast } from "sonner";
import { api, errMsg } from "../../lib/api";
import { useStore } from "../../context/StoreContext";

export function GoogleButton() {
  const startGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/hesabim";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };
  return (
    <button type="button" onClick={startGoogle}
      className="w-full h-12 rounded-full border-2 border-[#E6E4DD] bg-white font-semibold text-sm hover:border-amber-600 transition-colors flex items-center justify-center gap-3"
      data-testid="google-login-btn">
      <svg width="17" height="17" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.27-4.74 3.27-8.1Z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23Z"/><path fill="#FBBC05" d="M5.84 14.1c-.22-.66-.35-1.36-.35-2.1s.13-1.44.35-2.1V7.06H2.18A11 11 0 0 0 1 12c0 1.77.43 3.45 1.18 4.94l3.66-2.84Z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84c.87-2.6 3.3-4.52 6.16-4.52Z"/></svg>
      Google ile Devam Et
    </button>
  );
}

export function AuthCallback() {
  const { setCustomer, refreshCart } = useStore();
  const location = useLocation();
  const navigate = useNavigate();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;
    const hash = location.hash;
    const sessionId = hash.match(/session_id=([^&]+)/)?.[1];
    if (!sessionId) { navigate("/giris"); return; }
    api.post("/auth/google/session", { session_id: sessionId })
      .then(async ({ data }) => {
        setCustomer(data);
        await refreshCart();
        window.history.replaceState(null, "", window.location.pathname);
        navigate("/hesabim", { state: { user: data } });
      })
      .catch(() => { toast.error("Google ile giriş başarısız"); navigate("/giris"); });
  }, [location.hash, navigate, setCustomer, refreshCart]);

  return (
    <div className="min-h-[50vh] flex items-center justify-center">
      <div className="text-center">
        <div className="w-10 h-10 border-3 border-amber-600 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
        <p className="text-sm text-neutral-500">Giriş yapılıyor...</p>
      </div>
    </div>
  );
}

function AuthShell({ title, subtitle, children }) {
  return (
    <div className="max-w-md mx-auto px-4 py-14">
      <div className="bg-white border border-[#E6E4DD] rounded-3xl p-8">
        <img src="/assets/bigeldi-logo.png" alt="BIGELDİ" className="h-11 w-auto object-contain mb-6" />
        <h1 className="font-display font-extrabold text-2xl mb-1">{title}</h1>
        <p className="text-sm text-neutral-500 mb-7">{subtitle}</p>
        {children}
      </div>
    </div>
  );
}

const inputCls = "w-full h-12 px-4 bg-[#FAF9F5] border border-[#E6E4DD] rounded-xl text-sm focus:outline-none focus:border-amber-600";

export function LoginPage() {
  const { login, customer } = useStore();
  const navigate = useNavigate();
  const [form, setForm] = useState({ email: "", password: "" });
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (customer) navigate("/hesabim"); }, [customer, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await login(form.email, form.password);
      toast.success("Hoş geldiniz!");
      navigate("/hesabim");
    } catch (err) {
      toast.error(errMsg(err));
    } finally { setBusy(false); }
  };

  return (
    <AuthShell title="Giriş Yap" subtitle="BIGELDİ hesabınıza giriş yapın">
      <Helmet><title>Giriş Yap | BIGELDİ</title></Helmet>
      <form onSubmit={submit} className="space-y-3">
        <input type="email" required placeholder="E-posta" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={inputCls} data-testid="login-email" />
        <input type="password" required placeholder="Şifre" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className={inputCls} data-testid="login-password" />
        <button type="submit" disabled={busy} className="w-full h-12 rounded-full bg-[#121214] text-white font-semibold hover:bg-amber-600 transition-colors disabled:opacity-50" data-testid="login-submit">
          {busy ? "Giriş yapılıyor..." : "Giriş Yap"}
        </button>
      </form>
      <div className="flex items-center gap-3 my-5"><div className="flex-1 h-px bg-[#E6E4DD]" /><span className="text-xs text-neutral-400">veya</span><div className="flex-1 h-px bg-[#E6E4DD]" /></div>
      <GoogleButton />
      <div className="flex justify-between text-xs mt-5">
        <Link to="/sifremi-unuttum" className="text-neutral-500 hover:text-amber-700" data-testid="login-forgot">Şifremi Unuttum</Link>
        <Link to="/kayit" className="font-semibold text-amber-700" data-testid="login-register-link">Hemen Üye Ol</Link>
      </div>
    </AuthShell>
  );
}

export function RegisterPage() {
  const { register, customer } = useStore();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "", phone: "" });
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (customer) navigate("/hesabim"); }, [customer, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await register(form);
      toast.success("Üyeliğiniz oluşturuldu, hoş geldiniz!");
      navigate("/hesabim");
    } catch (err) {
      toast.error(errMsg(err));
    } finally { setBusy(false); }
  };

  return (
    <AuthShell title="Üye Ol" subtitle="Kampanyalardan ilk siz haberdar olun">
      <Helmet><title>Üye Ol | BIGELDİ</title></Helmet>
      <form onSubmit={submit} className="space-y-3">
        <input required placeholder="Ad Soyad" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} data-testid="register-name" />
        <input type="email" required placeholder="E-posta" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={inputCls} data-testid="register-email" />
        <input placeholder="Telefon (opsiyonel)" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className={inputCls} data-testid="register-phone" />
        <input type="password" required minLength={6} placeholder="Şifre (en az 6 karakter)" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className={inputCls} data-testid="register-password" />
        <button type="submit" disabled={busy} className="w-full h-12 rounded-full bg-[#121214] text-white font-semibold hover:bg-amber-600 transition-colors disabled:opacity-50" data-testid="register-submit">
          {busy ? "Hesap oluşturuluyor..." : "Üye Ol"}
        </button>
      </form>
      <div className="flex items-center gap-3 my-5"><div className="flex-1 h-px bg-[#E6E4DD]" /><span className="text-xs text-neutral-400">veya</span><div className="flex-1 h-px bg-[#E6E4DD]" /></div>
      <GoogleButton />
      <p className="text-xs text-neutral-500 mt-5 text-center">Zaten üye misiniz? <Link to="/giris" className="font-semibold text-amber-700" data-testid="register-login-link">Giriş Yap</Link></p>
    </AuthShell>
  );
}

export function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [done, setDone] = useState(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/auth/forgot-password", { email });
      setDone(data);
    } catch (err) {
      toast.error(errMsg(err));
    } finally { setBusy(false); }
  };

  return (
    <AuthShell title="Şifremi Unuttum" subtitle="E-posta adresinize sıfırlama bağlantısı gönderilecek">
      <Helmet><title>Şifremi Unuttum | BIGELDİ</title></Helmet>
      {done ? (
        <div className="text-sm text-neutral-600 space-y-3" data-testid="forgot-success">
          <p className="bg-emerald-50 border border-emerald-200 text-emerald-800 rounded-xl p-4">{done.message}</p>
          {done.debug_reset_link && (
            <p className="bg-amber-50 border border-amber-200 text-amber-800 rounded-xl p-4 text-xs break-all">
              <b>TEST:</b> E-posta servisi FAZ 2'de bağlanacak. Bağlantı:{" "}
              <Link className="underline font-semibold" to={new URL(done.debug_reset_link).pathname + new URL(done.debug_reset_link).search} data-testid="debug-reset-link">Şifreyi sıfırla</Link>
            </p>
          )}
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-3">
          <input type="email" required placeholder="E-posta" value={email} onChange={(e) => setEmail(e.target.value)} className={inputCls} data-testid="forgot-email" />
          <button type="submit" disabled={busy} className="w-full h-12 rounded-full bg-[#121214] text-white font-semibold hover:bg-amber-600 transition-colors disabled:opacity-50" data-testid="forgot-submit">
            {busy ? "Gönderiliyor..." : "Bağlantı Gönder"}
          </button>
        </form>
      )}
    </AuthShell>
  );
}

export function ResetPasswordPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token: params.get("token"), password });
      toast.success("Şifreniz güncellendi, giriş yapabilirsiniz");
      navigate("/giris");
    } catch (err) {
      toast.error(errMsg(err));
    } finally { setBusy(false); }
  };

  return (
    <AuthShell title="Yeni Şifre" subtitle="Yeni şifrenizi belirleyin">
      <Helmet><title>Şifre Sıfırla | BIGELDİ</title></Helmet>
      <form onSubmit={submit} className="space-y-3">
        <input type="password" required minLength={6} placeholder="Yeni şifre" value={password} onChange={(e) => setPassword(e.target.value)} className={inputCls} data-testid="reset-password" />
        <button type="submit" disabled={busy} className="w-full h-12 rounded-full bg-[#121214] text-white font-semibold hover:bg-amber-600 transition-colors disabled:opacity-50" data-testid="reset-submit">
          Şifremi Güncelle
        </button>
      </form>
    </AuthShell>
  );
}
